import _sortBy from 'lodash/sortBy'
import _map from 'lodash/map'
import _filter from 'lodash/filter'
import log from 'loglevel'
import {parseUserData, parseGroupData, parseApplicationData} from './index'
import {
    getUserQuery,
    getGroupQuery,
    getApplicationQuery,
    searchGroups,
    getGroupsForUser,
    searchUsers,
    getEntitlementQuery,
    searchApplicationsQuery,
    getApplicationsOwnedByUserQuery
} from './queries'
import queryProfile from './queryProfile'

const limit = 200

const getUserById = async (id, includeInactive, tokenProvider, papiUrl) => {
    try {
        const {user} = await queryProfile(
            getUserQuery(id, null, includeInactive),
            tokenProvider,
            papiUrl
        )
        if (user) {
            return parseUserData(user)
        }
        log.error(`user ${id} was not found`)
        return {id, name: 'DOES-NOT-EXIST', employeeType: 'Unknown'}
    } catch (err) {
        log.error(err)
        return {id, name: id}
    }
}

const getGroupById = async (id, tokenProvider, papiUrl) => {
    try {
        const {group} = await queryProfile(getGroupQuery(id), tokenProvider, papiUrl)
        if (group) {
            return group
        }
        log.error(`group ${id} was not found`)
        return {id, name: 'DOES-NOT-EXIST'}
    } catch (err) {
        return {id}
    }
}
const getApplicationById = async (id, tokenProvider, papiUrl) => {
    try {
        const {application} = await queryProfile(getApplicationQuery(id), tokenProvider, papiUrl)
        if (application) {
            return application
        }

        return {id, name: 'DOES-NOT-EXIST'}
    } catch (err) {
        log.error(err)
        return {id}
    }
}

const getEntitlementById = async (id, tokenProvider, papiUrl) => {
    try {
        const {entitlement} = await queryProfile(getEntitlementQuery(id), tokenProvider, papiUrl)
        if (entitlement) {
            return entitlement
        }
        log.error(`entitlement ${id} was not found`)
        return {id, name: 'DOES-NOT-EXIST'}
    } catch (err) {
        log.error(err)
        return {id}
    }
}

const fetchUsers = async (input, nonSelectableItems, customFields, tokenProvider, papiUrl) => {
    if (input) {
        try {
            const queryFn = searchUsers(input, customFields)
            const data = await queryProfile(queryFn, tokenProvider, papiUrl)
            return _sortBy(
                _map(data.result.users, (user) => parseUserData(user, nonSelectableItems)),
                ['name', 'id']
            )
        } catch (e) {
            log.error(e)
            return Promise.resolve([])
        }
    }
    return Promise.resolve([])
}

const fetchGroups = (
    input,
    nonSelectableItems,
    hideUnrequestable,
    isMember,
    customFields,
    tokenProvider,
    papiUrl
) => {
    const groupFilter = hideUnrequestable
        ? (group) => group.strategy.toLowerCase() !== 'unrequestable'
        : () => true
    const retriever = isMember
        ? getGroupsForUser(input, customFields)
        : searchGroups(input, customFields)
    return queryProfile(retriever, tokenProvider, papiUrl)
        .then((data) =>
            _sortBy(
                _filter(
                    _map(data.result.groups, (group) => parseGroupData(group, nonSelectableItems)),
                    groupFilter
                ),
                ['name', 'id']
            )
        )
        .catch((e) => {
            log.error(e)
            return Promise.resolve([])
        })
}

const getUserOwnedApplications = async (input, customFields, tokenProvider, papiUrl) => {
    const query = getApplicationsOwnedByUserQuery(input, customFields)
    const {result} = await queryProfile(query, tokenProvider, papiUrl)
    return result.applications
}

/**
 * Finds applications based on the provided input. It uses PAPI's searchApplications query to
 * find only applications which names contain provided input string.
 * @param input string entered by the user
 * @returns {Promise<*>} resolves to a list of application objects containing id and name
 */
const searchApplications = async (input, tokenProvider, papiUrl) => {
    const fetchedApplications = []
    let offset = 0
    let total = 0
    let totalCount = 0
    do {
        const query = searchApplicationsQuery(input, offset, limit)
        const resultPage = await queryProfile(query, tokenProvider, papiUrl)
        const {searchResult} = resultPage
        total = searchResult.total
        totalCount += searchResult.count
        fetchedApplications.push(...searchResult.applications)
        offset += searchResult.count
    } while (totalCount < total)
    return fetchedApplications
}

const fetchApplications = async (input, isOwner, customFields, tokenProvider, profileApiUrl) => {
    const apps = isOwner
        ? await getUserOwnedApplications(input, customFields, tokenProvider, profileApiUrl)
        : await searchApplications(input, tokenProvider, profileApiUrl)
    return _sortBy(_map(apps, parseApplicationData), ['name', 'id'])
}

export {
    getUserById,
    getGroupById,
    getApplicationById,
    getEntitlementById,
    fetchApplications,
    fetchGroups,
    fetchUsers,
    searchApplications
}
